import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { formatInstantForViewer, getViewerTimezone, resolveTimezoneFromState } from '../utils/dateTime';
import AssignJobModal from '../components/jobs/AssignJobModal';
import DeclineJobConfirmModal from '../components/jobs/DeclineJobConfirmModal';
import { subscribeToSrEvents } from '../services/webPushService';
import './JobsPage.css';
import './TransactionsPage.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const TAB_STATUSES = {
  requests:  ['PENDING', 'AWAITING_ASSIGNMENT'],
  active:    ['ACCEPTED', 'SCHEDULED', 'IN_PROGRESS'],
  completed: ['COMPLETED'],
  // Both DECLINED (SP said no) and CANCELLED (requester withdrew) mean
  // "SR ended without the job happening" — bucket them together under
  // Declined. COMPLETED stays alone since it means the job actually got
  // done, which is semantically distinct. Product 2026-08-31 EOD: "SR
  // cancel kr diya to SP company and inspector ke side decline tab me
  // sift hona chiye with cancel status".
  declined:  ['DECLINED', 'CANCELLED'],
};

const ACTION_ROW = {
  AWAITING_ASSIGNMENT: { label: 'Needs assignment', cls: 'action-danger' },
  PENDING:     { label: 'Action required',  cls: 'action-danger' },
  ACCEPTED:    { label: 'Confirm schedule', cls: 'action-primary' },
  SCHEDULED:   { label: 'Awaiting access',  cls: 'action-primary' },
  IN_PROGRESS: { label: 'Upload report',    cls: 'action-primary' },
  COMPLETED:   { label: 'Completed',        cls: 'action-success' },
  DECLINED:    { label: 'Declined',         cls: 'action-muted' },
  CANCELLED:   { label: 'Cancelled',        cls: 'action-muted' },
};

// Statuses that are already resolved — a past due date on one of these isn't
// "Overdue", it's just history (a completed job whose due date was last week
// isn't an alarm).
const TERMINAL_STATUSES = ['COMPLETED', 'DECLINED', 'CANCELLED'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const getFirebaseToken = () =>
  new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken(true).then(resolve).catch(reject);
      else reject(new Error('Not authenticated'));
    });
    setTimeout(() => { unsub(); reject(new Error('Auth timeout')); }, 10000);
  });

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      timeZone: getViewerTimezone(),
    });
  } catch { return String(iso); }
}

// "Aug 15, 2026 · 2:30 PM". requestedTime may be a full ISO datetime (backend
// currently sends "2026-08-20T13:16:04.145Z") or an "HH:mm[:ss]" string; when
// absent, lift the time off a full-ISO requestedDate. Skips the time part if
// requestedDate is a bare YYYY-MM-DD (parses to midnight LOCAL — showing that
// as "12:00 AM" would be misleading).
//
// Full-ISO values (the common case — requestedDate/requestedTime are always
// real UTC instants) render in the *viewing* user's own profile timezone via
// formatInstantForViewer, with a zone indicator appended. The bare "HH:mm"
// fallback (legacy/calendar-event-sourced data with no embedded zone) keeps
// the previous device-local rendering — there's no real instant to convert.
function fmtDateTime(dateIso, timeStr, viewerTimezone) {
  if (!dateIso) return '';
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return String(dateIso);
  const isFullIsoDate = String(dateIso).indexOf('T') >= 0;
  const datePart = isFullIsoDate
    ? formatInstantForViewer(dateIso, viewerTimezone).dateLabel
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeOpts = { hour: 'numeric', minute: '2-digit', hour12: true };
  let timePart = '';
  if (timeStr) {
    const asIso = new Date(timeStr);
    if (!Number.isNaN(asIso.getTime()) && String(timeStr).indexOf('T') >= 0) {
      timePart = formatInstantForViewer(timeStr, viewerTimezone).timeLabel;
    } else {
      const composed = new Date(`1970-01-01T${timeStr}`);
      if (!Number.isNaN(composed.getTime())) {
        // Bare "HH:mm" fallback — no real instant to zone-convert. Keep
        // device-local behavior (the whole 1970 anchor Date is a wall-clock
        // placeholder, not an actual moment in time).
        timePart = composed.toLocaleTimeString('en-US', timeOpts);
      }
    }
  } else if (isFullIsoDate) {
    timePart = formatInstantForViewer(dateIso, viewerTimezone).timeLabel;
  }
  return timePart ? `${datePart} · ${timePart}` : datePart;
}

// Backend sometimes leaves the requester's display name unresolved and sends
// the raw Firestore document ID instead (e.g. "NOCjI25skgXkydSf7mSE") — a bare
// alphanumeric key with no spaces, unlike any real person's name. Filter those
// out so the UI falls back to "Agent" rather than leaking an internal key.
function isRawIdLike(str) {
  return typeof str === 'string' && /^[A-Za-z0-9]{15,36}$/.test(str) && /\d/.test(str);
}
function cleanName(v) {
  return (v && !isRawIdLike(v)) ? v : undefined;
}

function normalizeJob(raw) {
  const status = raw.currentStatus || raw.status || '';
  return {
    id:          raw.serviceId || raw.id || raw.requestId || raw.serviceRequestId,
    serviceType: raw.serviceType || 'Service',
    status,
    address:     raw.propertyAddress
                   || raw.address
                   || (raw.property && raw.property.address)
                   || 'Unknown address',
    // Card date = SR's due date (requestedDate), NOT the creation
    // timestamp. The 2026-09-11 refactor swapped this to createdAt to
    // avoid a "Sep 8 · 11:59 PM" clutter now that Due-By-Time was
    // removed — but that swap silently collapsed every vendor's jobs
    // onto the day their SRs were created regardless of the actual due
    // date (a Cleaning SR due 28 Sep created on 21 Sep displayed as
    // 21 Sep on the vendor's card). Reported 2026-09-29.
    //
    // Passing time=null keeps the card free of the 11:59 PM clutter for
    // post-2026-09-11 SRs (which no longer carry requestedTime). Legacy
    // SRs with a real requestedTime still render date + real time. Mirrors
    // the SP mobile app fix.
    date:        raw.requestedDate || null,
    time:        null,
    // Actual due-by instant — drives the Overdue badge (isPastDue below).
    // Same source as `date` now (was intentionally split when `date` used
    // createdAt; no longer needed but kept for the isPastDue read shape).
    dueBy:       raw.requestedDate || raw.dueDate || null,
    clientName:  cleanName(raw.requesterName)
                   || cleanName(raw.requestedBy)
                   || cleanName(raw.agentName)
                   || [raw.agentFirstName, raw.agentLastName].filter(Boolean).join(' ')
                   || 'Agent',
    description:  raw.description || '',
    urgencyLevel: (raw.urgencyLevel || '').toUpperCase(),
    // Currently-assigned provider — used to detect a PENDING job routed to an
    // on-staff inspector (providerId differs from the company's own id) so
    // the Company can Re-Assign it before it's accepted.
    providerId:   raw.providerId || null,
    providerName: cleanName(raw.providerName) || null,
    // Bounce-back marker: when a Company-routed SR was declined by an
    // inspector, backend flips status → AWAITING_ASSIGNMENT and moves that
    // inspector's id into previousProviderId. The inspector's Declined tab
    // needs this field to recognise their own bounce history (see the
    // declined-tab filter below).
    previousProviderId: raw.previousProviderId || null,
  };
}

function extractArray(data) {
  if (Array.isArray(data)) return data;
  return data?.services || data?.content || data?.data || [];
}

// True once a job's due date is in the past and it's still in an unresolved
// status — drives the "Overdue" badge below. Uses job.dueBy (the SR's
// requestedDate) rather than job.date (the SR's createdAt, which is what
// the card subtitle shows) so the badge fires against the real deadline
// instead of firing on every non-terminal SR the moment it exists.
function isPastDue(job) {
  if (!job.dueBy || TERMINAL_STATUSES.includes(job.status)) return false;
  const t = new Date(job.dueBy).getTime();
  return !Number.isNaN(t) && t < Date.now();
}

// Latest due date first (descending). Jobs without a parseable due date sort
// last (there's nothing to rank them by), and ties fall back to id so the
// order stays identical across re-fetches — this is also what keeps a
// reassigned job (providerId changes, due date doesn't) from appearing to
// "jump" in the list.
function compareByDueDate(a, b) {
  const ta = a.date ? new Date(a.date).getTime() : NaN;
  const tb = b.date ? new Date(b.date).getTime() : NaN;
  const aValid = !Number.isNaN(ta);
  const bValid = !Number.isNaN(tb);
  if (aValid && bValid && ta !== tb) return tb - ta;
  if (aValid !== bValid) return aValid ? -1 : 1;
  return String(a.id).localeCompare(String(b.id));
}

// ── SVG icons ─────────────────────────────────────────────────────────────────
const BriefcaseIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  </svg>
);
const FlashIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

// ── Main page component ───────────────────────────────────────────────────────
const JobsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(
    TAB_STATUSES[location.state?.tab] ? location.state.tab : 'requests'
  );

  const [jobs, setJobs]               = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError]     = useState(null);

  const [pendingCount, setPendingCount] = useState(0);
  const [retryKey, setRetryKey]         = useState(0);

  // Company vendorType gates the extra roster-jobs merge below (an Individual
  // SP has no roster, so this just stays false and the merge is a no-op).
  // Tri-state: `null` = still loading (spProfile fetch not resolved yet),
  // `true` = Company, `false` = Individual / non-SP. The jobs fetch effect
  // waits for `null` → boolean transition before firing, so a Company SP
  // doesn't get two rounds of spServices() calls (one at default false,
  // one after the fetch flips it).
  const [isCompanySp, setIsCompanySp] = useState(null);
  // Self DB id — same "id" field SpDashboardHeader.js reads off the stored
  // user profile. Used to tell "this PENDING job is still the company's own"
  // apart from "it's been routed to one of our inspectors" (job.providerId
  // differs from our own id), which is what gates the Re-Assign action.
  const [companyUserId, setCompanyUserId] = useState(null);

  // Decline-confirm modal (Company SP terminal-declining an SR without
  // inspector routing). Non-null while the modal is open; onConfirm hits
  // the backend and the child dialog owns its own submitting/spinner state
  // so the JobsPage card doesn't need a spinner of its own.
  const [declineJob, setDeclineJob] = useState(null);

  // Assign-to-provider modal (Company SP routing an AWAITING_ASSIGNMENT job
  // to one of their on-staff individual providers, or re-routing a PENDING
  // job already assigned to one of them but not yet accepted)
  const [assignJob, setAssignJob]             = useState(null);
  const [assignMode, setAssignMode]           = useState('assign'); // 'assign' | 'reassign'
  const [vendors, setVendors]                 = useState(null);
  const [vendorsLoading, setVendorsLoading]   = useState(false);
  const [vendorsError, setVendorsError]       = useState(null);

  // The viewing SP's own timezone, derived from their Service Provider
  // profile's State (see the spProfile fetch below) — the base user/
  // localStorage `state` isn't reliably synced for SP accounts, since
  // SpProfilePage saves through /users/me/sp-profile instead. Starts from
  // the generic localStorage-based fallback so nothing renders blank before
  // the fetch resolves.
  const [viewerTimezone, setViewerTimezone] = useState(() => getViewerTimezone());

  // Role guard
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Determine once whether this SP account is a Company vendorType, to gate
  // the roster-jobs merge below. Same lookup SpDashboardHeader.js does.
  // Always flip isCompanySp from null → boolean when the fetch settles, even
  // on failure, so the gated jobs effect doesn't sit blocked forever.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setIsCompanySp((resp.data?.vendorType || '').toUpperCase() === 'COMPANY');
          if (typeof resp.data?.state === 'string' && resp.data.state.trim()) {
            setViewerTimezone(resolveTimezoneFromState(resp.data.state));
          }
        }
      } catch {
        // Non-critical — roster-jobs merge just stays off. Flip to false so
        // the jobs effect below unblocks. viewerTimezone keeps its
        // localStorage-based initial value.
        if (!cancelled) setIsCompanySp(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Self DB id, read off the same stored profile SpDashboardHeader.js uses —
  // see companyUserId comment above.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (stored.id) setCompanyUserId(stored.id);
    } catch {
      // Non-critical — Re-Assign just stays hidden without a self id to compare.
    }
  }, []);

  // Fetch jobs for the active tab (retryKey forces re-fetch on retry).
  // Waits for isCompanySp to settle from its null loading state so a Company
  // SP doesn't get two rounds of fetches (one with the roster-jobs branch
  // off at default false, one with it on after spProfile resolves).
  useEffect(() => {
    if (isCompanySp === null) return;
    let cancelled = false;
    setJobsLoading(true);
    setJobsError(null);

    (async () => {
      try {
        const token = await getFirebaseToken();
        const statuses = TAB_STATUSES[activeTab];
        // Backend accepts a comma-separated status list (2026-08-31), so
        // collapse the per-status fan-out into one call. Single-endpoint
        // fetch: backend ServiceRequestService.findByProviderId now
        // auto-unions the Company-roster jobs for Company-SP callers, so
        // the client no longer needs to also hit /service-provider/company/
        // jobs and merge — one call, one payload. Individual SPs get the
        // same data they always did.
        const statusParam = statuses.join(',');
        const results = await apiClient
          .get(API_ENDPOINTS.spServices(statusParam), {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(r => extractArray(r.data))
          .catch(() => []);
        if (cancelled) return;
        const tabStatuses = TAB_STATUSES[activeTab];
        const seenIds = new Set();
        const allJobs = results
          .map(normalizeJob)
          .filter(job => {
            if (tabStatuses.includes(job.status)) {
              // A bounced-back SR carries AWAITING_ASSIGNMENT which the
              // Requests tab's status filter would otherwise let through —
              // but from the declining inspector's POV this is a job they
              // already turned down, so it belongs only on the Declined
              // tab (unioned in below), not back on the fresh-work list.
              // Recognise via previousProviderId == self.
              if (activeTab === 'requests'
                  && job.status === 'AWAITING_ASSIGNMENT'
                  && job.previousProviderId
                  && companyUserId
                  && job.previousProviderId === companyUserId) {
                return false;
              }
              return true;
            }
            // Backend unions bounced-back SRs into the Declined-tab
            // response for the inspector who originally declined them
            // (status flips to AWAITING_ASSIGNMENT but the SR belongs on
            // the Declined tab from that inspector's POV). Recognise
            // them here so the client doesn't drop them. Guard on
            // `previousProviderId` presence — a Company on their own
            // Declined tab never has previousProviderId set on their
            // items, so this branch doesn't leak cross-tab.
            if (activeTab === 'declined'
                && job.status === 'AWAITING_ASSIGNMENT'
                && job.previousProviderId) {
              return true;
            }
            return false;
          })
          .filter(job => (seenIds.has(job.id) ? false : (seenIds.add(job.id), true)));
        allJobs.sort(compareByDueDate);
        setJobs(allJobs);
        if (activeTab === 'requests') setPendingCount(allJobs.length);
      } catch {
        if (!cancelled) setJobsError('Failed to load jobs. Please try again.');
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [activeTab, retryKey, isCompanySp, companyUserId]);

  // Auto-refresh via focus/visibility was removed 2026-08-31 EOD after
  // user report: "automatic api call nahi karawo". SP-side data now
  // refreshes ONLY on initial mount, tab change, and manual retry
  // (`handleRetry`). If the SP needs to see a recent cancel/accept
  // land, they hit the reload / retry button. Trade-off: no
  // "requester cancelled while I was away" catch-up unless user acts.

  // Fetch pending count for the badge when on a non-requests tab
  useEffect(() => {
    if (activeTab === 'requests') return;
    getFirebaseToken()
      .then(token =>
        apiClient.get(API_ENDPOINTS.spServices('PENDING'), {
          headers: { Authorization: `Bearer ${token}` },
        })
      )
      .then(r => setPendingCount(extractArray(r.data).length))
      .catch(() => {});
  }, [activeTab]);

  const handleRetry = () => setRetryKey(k => k + 1);

  // FCM foreground listener — refresh the jobs list whenever any SR
  // lifecycle push targets this user (requester cancelled, SR accepted
  // by another company inspector, decline reflow, etc.). Push-based
  // only; polling / focus refresh was rejected 2026-08-31 EOD. Silent
  // no-op when web push isn't wired up (permission denied, missing
  // VAPID key, etc.) — behavior degrades to the existing fresh-fetch-
  // on-navigate + failed-click refetch pattern.
  useEffect(() => {
    return subscribeToSrEvents(() => {
      setRetryKey((k) => k + 1);
    });
  }, []);

  const handleJobClick = (job) => {
    navigate(`/job/${job.id}`, { state: { job } });
  };

  const openAssignModal = async (job, mode = 'assign') => {
    setAssignJob(job);
    setAssignMode(mode);
    setVendorsLoading(true);
    setVendorsError(null);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.companyVendors, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawList = extractArray(res.data);
      let vendorList = rawList;
      // Re-assign: drop the inspector the job is already sitting with —
      // picking them again is a no-op the backend rejects with 400.
      if (mode === 'reassign' && job.providerId) {
        vendorList = vendorList.filter((v) => v.userId !== job.providerId);
      }
      // Bounced-for-reassign: mode is 'assign' (SR is back at AWAITING_ASSIGNMENT
      // after an inspector decline), and previousProviderId holds the declined
      // inspector's id. Filter them out so the dispatcher can't route the same
      // job back to the same person.
      if (job.previousProviderId) {
        vendorList = vendorList.filter((v) => v.userId !== job.previousProviderId);
      }
      // Distinguish "no SPs at all" from "all SPs filtered out" so the
      // empty-state message is accurate. If the raw list had entries but
      // the filters emptied it, the roster isn't the problem — the
      // dispatcher has no eligible pick given the job's history.
      setVendors(vendorList);
      if (vendorList.length === 0 && rawList.length > 0) {
        setVendorsError(job.previousProviderId
          ? 'The inspector who already declined this SR is the only one on your roster. Add another inspector or decline the SR.'
          : 'The inspector this job is currently assigned to is the only one on your roster. Add another inspector to re-assign.');
      }
    } catch {
      setVendorsError('Failed to load your service providers. Please try again.');
    } finally {
      setVendorsLoading(false);
    }
  };

  const closeAssignModal = () => {
    setAssignJob(null);
    setAssignMode('assign');
    setVendors(null);
    setVendorsError(null);
  };

  const handleAssignSubmit = async (inspectorUserId) => {
    const token = await getFirebaseToken();
    const endpoint = assignMode === 'reassign'
      ? API_ENDPOINTS.reassignInspector(assignJob.id)
      : API_ENDPOINTS.assignInspector(assignJob.id);
    await apiClient.patch(
      endpoint,
      { inspectorUserId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    closeAssignModal();
    // Assign: the job leaves the Requests tab (AWAITING_ASSIGNMENT → PENDING
    // under the inspector). Re-assign: providerId moves to the new inspector
    // but status stays PENDING, so the card stays put with a fresh provider —
    // either way, refetch to pick up the change.
    setRetryKey((k) => k + 1);
  };

  // Company-level decline — terminates the SR without any inspector routing.
  // Rows 1, 2 and 5 in the lifecycle grid: from AWAITING_ASSIGNMENT (initial
  // or post-bounce) or company-routed PENDING. Card button just opens the
  // DeclineJobConfirmModal; the modal owns submitting/spinner state and
  // calls handleDeclineSubmit with the optional reason. Backend flips
  // status to DECLINED and fires SR_COMPANY_DECLINED to the requester
  // (their timeline flips to a Declined pill + surfaces "Reassign SP" for
  // picking a different Company or Individual).
  const openDeclineModal = (job) => setDeclineJob(job);
  const closeDeclineModal = () => setDeclineJob(null);
  const handleDeclineSubmit = async (reason) => {
    const token = await getFirebaseToken();
    await apiClient.patch(
      API_ENDPOINTS.companyDeclineRequest(declineJob.id),
      reason ? { reason } : {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    closeDeclineModal();
    setRetryKey((k) => k + 1);
  };

  return (
    <div className="trans-page-container">
      <div className="trans-main-content">
        <div className="trans-content-box">
          {/* Page Header — no "New Transaction"-style creation action for Jobs */}
          <div className="trans-page-header">
            <h1 className="trans-page-title">Jobs</h1>
          </div>

          {/* Filter Tabs */}
          <div className="trans-filter-tabs">
            {[
              { key: 'requests',  label: 'Requests',  badge: pendingCount },
              { key: 'active',    label: 'Active' },
              { key: 'completed', label: 'Completed' },
              { key: 'declined',  label: 'Declined' },
            ].map(({ key, label, badge }) => (
              <button
                key={key}
                className={`trans-filter-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
                {badge !== undefined && badge > 0 && (
                  <span className="trans-filter-tab-badge">{badge}</span>
                )}
              </button>
            ))}
          </div>

          {/* Jobs Grid */}
          {jobsLoading ? (
            <div className="trans-grid">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="trans-skeleton-card">
                  <div className="trans-skeleton-header">
                    <div className="trans-skeleton-icon" />
                    <div className="trans-skeleton-lines">
                      <div className="trans-skeleton-line trans-skeleton-line--title" />
                      <div className="trans-skeleton-line trans-skeleton-line--sub" />
                    </div>
                  </div>
                  <div className="trans-skeleton-line trans-skeleton-line--badge" />
                  <div className="trans-skeleton-footer">
                    <div className="trans-skeleton-line trans-skeleton-line--date" />
                  </div>
                </div>
              ))}
            </div>
          ) : jobsError ? (
            <div className="trans-empty-state">
              <div className="trans-empty-icon trans-empty-icon--error">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2.5"/></svg>
              </div>
              <h3 className="trans-empty-title">Failed to Load</h3>
              <p className="trans-empty-subtitle">{jobsError}</p>
              <button className="trans-retry-btn" onClick={handleRetry}>Try Again</button>
            </div>
          ) : jobs.length > 0 ? (
            <>
              <div className="trans-results-count">
                {jobs.length} job{jobs.length !== 1 ? 's' : ''}
              </div>
              <div className="trans-grid">
                {jobs.map((job) => {
                  // Past due date on an unresolved job overrides whatever the
                  // status would normally show (e.g. "Needs assignment") —
                  // the missed date is the more urgent fact.
                  // Bounced-for-reassign: AWAITING_ASSIGNMENT arrived here via
                  // an inspector-decline reflow (backend stamps
                  // previousProviderId). Show "Reassign Needed" instead of
                  // generic "Needs assignment" so the dispatcher sees this is
                  // a bounce that needs action, not a fresh job.
                  const isBouncedForReassign = job.status === 'AWAITING_ASSIGNMENT'
                    && !!job.previousProviderId;
                  const action = isPastDue(job)
                    ? { label: 'Overdue', cls: 'action-danger' }
                    : isBouncedForReassign
                      ? { label: 'Reassign Needed', cls: 'action-danger' }
                      : (ACTION_ROW[job.status] || { label: job.status, cls: 'action-muted' });
                  // "Completed" and "Declined" duplicate the card's own tab/state,
                  // so this badge is suppressed for those two statuses.
                  const showActionBadge = action.label !== 'Completed' && action.label !== 'Declined';
                  const isUrgent = job.urgencyLevel === 'HIGH' || job.urgencyLevel === 'URGENT';
                  return (
                    <div
                      key={job.id}
                      className="trans-card"
                      onClick={() => handleJobClick(job)}
                    >
                      <div className="trans-card-header">
                        <div className="trans-property-icon">
                          <BriefcaseIcon size={22} />
                        </div>
                        <div className="trans-card-info">
                          <div className="trans-property-address">{job.address}</div>
                          {job.description && (
                            <div className="jobs-card-description">{job.description}</div>
                          )}
                        </div>
                        <div className="jobs-card-badge-col">
                          {isUrgent && (
                            <span className="trans-milestone-badge job-urgent-badge">
                              <FlashIcon />Urgent
                            </span>
                          )}
                          {showActionBadge && (
                            <span className={`trans-milestone-badge ${action.cls}`}>{action.label}</span>
                          )}
                        </div>
                      </div>

                      <div className="trans-card-meta">
                        <span className="trans-meta-info">{job.date ? fmtDateTime(job.date, job.time, viewerTimezone) : ''}</span>
                        <span className="trans-arrow-icon">→</span>
                      </div>

                      {/* Assign + Decline appear when the Company hasn't
                          yet routed the SR to a staff inspector. That's
                          normally AWAITING_ASSIGNMENT, but some SRs land
                          directly in PENDING with the Company as
                          providerId (backend `isCompanyProfile` returns
                          false when User.vendorType isn't set — even
                          though the legacy ServiceProviderProfile flags
                          the account as COMPANY). Both shapes mean "Company
                          needs to route or decline", so treat them the
                          same here.
                          Suppressed on the Declined tab: bounced-back
                          entries also land there as AWAITING_ASSIGNMENT
                          historical rows from the inspector's POV, and
                          they're read-only (the Company owns them now,
                          not the viewing inspector). */}
                      {activeTab !== 'declined'
                        && (job.status === 'AWAITING_ASSIGNMENT'
                            || (job.status === 'PENDING' && isCompanySp
                                && job.providerId && job.providerId === companyUserId)) && (
                        <div className="jp-action-row">
                          <button
                            type="button"
                            className="jp-assign-btn"
                            onClick={(e) => { e.stopPropagation(); openAssignModal(job, 'assign'); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                            {isBouncedForReassign ? 'Reassign' : 'Assign'}
                          </button>
                          {/* Row 1 + Row 5: company can also decline
                              outright, not just route to an inspector. */}
                          <button
                            type="button"
                            className="jp-decline-btn"
                            onClick={(e) => { e.stopPropagation(); openDeclineModal(job); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                            Decline Request
                          </button>
                        </div>
                      )}

                      {/* Re-Assign: only once the job is already assigned to
                          one of this Company's inspectors and they haven't
                          accepted it yet (still PENDING). Company can also
                          decline outright at this stage (Row 2 in the
                          lifecycle grid). Not applicable on the Declined
                          tab — that view is historical. */}
                      {activeTab !== 'declined'
                        && job.status === 'PENDING' && isCompanySp
                        && job.providerId && job.providerId !== companyUserId && (
                        <div className="jp-action-row">
                          <button
                            type="button"
                            className="jp-assign-btn"
                            onClick={(e) => { e.stopPropagation(); openAssignModal(job, 'reassign'); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                            Re-Assign
                          </button>
                          <button
                            type="button"
                            className="jp-decline-btn"
                            onClick={(e) => { e.stopPropagation(); openDeclineModal(job); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                            Decline Request
                          </button>
                        </div>
                      )}

                      {/* Phase 1 UX: once the routed inspector has ACCEPTED
                          (or moved further), the Re-Assign button must stay
                          visible on the card but locked with a tooltip —
                          per spec, Reassign never disappears, only toggles
                          enabled/disabled. Decline is intentionally omitted
                          here (spec only governs Reassign visibility). */}
                      {activeTab !== 'declined'
                        && isCompanySp
                        && job.providerId && job.providerId !== companyUserId
                        && ['ACCEPTED', 'IN_PROGRESS', 'COMPLETED'].includes(job.status) && (
                        <div className="jp-action-row">
                          <button
                            type="button"
                            className="jp-assign-btn"
                            disabled
                            aria-disabled="true"
                            title="Reassign unavailable — provider has accepted this job."
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                            Re-Assign
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="trans-empty-state">
              <div className="trans-empty-icon">
                <BriefcaseIcon size={40} />
              </div>
              <h3 className="trans-empty-title">No Jobs</h3>
              <p className="trans-empty-subtitle">You don't have any jobs in this category yet.</p>
            </div>
          )}
        </div>
      </div>

      <AssignJobModal
        visible={!!assignJob}
        job={assignJob}
        mode={assignMode}
        vendors={vendors}
        vendorsLoading={vendorsLoading}
        vendorsError={vendorsError}
        onClose={closeAssignModal}
        onAssign={handleAssignSubmit}
      />
      <DeclineJobConfirmModal
        visible={!!declineJob}
        job={declineJob}
        onClose={closeDeclineModal}
        onConfirm={handleDeclineSubmit}
      />
    </div>
  );
};

export default JobsPage;
